import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { inject, Injectable } from '@angular/core';
import { SubscriptionService } from '../services/subscription.service';
import { SubscriptionStatusTypes } from '../models/subscription-status.types';
import { AuthService } from '../services/auth.service';
import { PricingPlan } from '../models/pricing-plan.interface';
import { OrgSubscription } from '../models/subscription.interface';
import { firstValueFrom } from 'rxjs';
import { UserRole } from '../models/user-role.types';

@Injectable({ providedIn: 'root' })
export class SubscriptionGuardService implements CanActivate {
  private router = inject(Router);
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const session = this.authService.getSession();
    if (!session) {
      this.router.navigate(['/login']);
      return false;
    }

    let user = this.authService.currentUser();
    let sub = this.subscriptionService.currentSubscription();

    // If session exists but user/sub are not loaded, wait for them.
    if (!user || !sub) {
      await this.authService.resolveUserFromSession();
      user = this.authService.currentUser();
      sub = this.subscriptionService.currentSubscription();
    }

    // Ensure pricing plans are loaded
    if (this.subscriptionService.pricingPlans().length === 0) {
      await firstValueFrom(this.subscriptionService.fetchPricingPlans());
    }

    // Super Admins bypass subscription checks (they manage the system)
    if (user?.role === UserRole.SUPER_ADMIN) {
      return true;
    }

    const path = route.routeConfig?.path;
    const isBillingPath = path?.includes('subscription-billing');
    const isDashboardPath = path === 'dashboard';
    const isSubRequiredPath = path === 'subscription-required';

    // Get showSubBadges from route data, default to true if not specified
    const showSubBadges = route.data['showSubBadges'] !== false;
    const showBackButton = route.data['showBackButton'];

    // Billing paths require ADMIN role (already handled by AdminGuardService, but double-check here)
    if (isBillingPath) {
      if (user?.role !== UserRole.ADMIN) {
        this.navigateToAdminRequired(path, showSubBadges, showBackButton);
        return false;
      }
      return true;
    }

    if (isDashboardPath || isSubRequiredPath) {
      return true;
    }

    if (!sub) {
      this.navigateToSubscriptionRequired(path, showSubBadges, showBackButton);
      return false;
    }

    const allowedStatuses: SubscriptionStatusTypes[] = [SubscriptionStatusTypes.ACTIVE, SubscriptionStatusTypes.TRIALING];
    if (!allowedStatuses.includes(sub.status)) {
      this.navigateToSubscriptionRequired(path, showSubBadges, showBackButton);
      return false;
    }

    // Check feature-level access based on the current pricing plan
    const plan = this.getCurrentPlan(sub.pricingPlanId);
    if (!plan) {
      return true;
    }

    if (!this.hasFeatureAccess(plan, path)) {
      this.navigateToUpgradeRequired(path, showSubBadges, showBackButton);
      return false;
    }

    if (this.hasReachedLimit(plan, sub, path)) {
      this.navigateToLimitReached(path, showSubBadges, showBackButton);
      return false;
    }

    return true;
  }

  private getCurrentPlan(pricingPlanId: number): PricingPlan | undefined {
    return this.subscriptionService.pricingPlans().find(p => p.id === pricingPlanId);
  }

  private hasFeatureAccess(plan: PricingPlan, path: string | undefined): boolean {
    if (!path) {
      return true;
    }

    if (path.includes('assistant')) {
      return plan.liveAssistantSeconds > 0;
    }

    if (path.includes('playground')) {
      return plan.coldCallSeconds > 0 || plan.emailPractices > 0;
    }

    if (path.includes('create-new-document') || path.includes('knowledge-base')) {
      return plan.maxDocuments > 0;
    }

    return true;
  }

  private hasReachedLimit(plan: PricingPlan, sub: OrgSubscription, path: string | undefined): boolean {
    if (!path || !sub.currentUsage) {
      return false;
    }

    const seats = sub.seats || 1;

    if (path.includes('assistant')) {
      const totalLimit = plan.liveAssistantSeconds * seats;
      return sub.currentUsage.liveAssistantSecondsUsed >= totalLimit;
    }

    if (path.includes('playground')) {
      const totalColdCallLimit = plan.coldCallSeconds * seats;
      const totalEmailLimit = plan.emailPractices * seats;

      if (path.includes('cold-call')) {
        return sub.currentUsage.coldCallSecondsUsed >= totalColdCallLimit;
      }
      if (path.includes('email-outreach')) {
        return sub.currentUsage.emailPracticesUsed >= totalEmailLimit;
      }

      return sub.currentUsage.coldCallSecondsUsed >= totalColdCallLimit && sub.currentUsage.emailPracticesUsed >= totalEmailLimit;
    }

    return false;
  }

  private navigateToSubscriptionRequired(path: string | undefined, showSubBadges: boolean, showBackButton?: boolean) {
    let title = 'Subscription Required';
    let description = 'This feature requires an active or trialing subscription. Please choose a plan that fits your business needs.';

    if (path?.includes('assistant')) {
      title = 'Live Assistant subscription missing';
      description = 'The live assistant feature is only available in a paid plan. Subscribe now to unlock real-time guidance and meeting summaries.';
    } else if (path?.includes('playground')) {
      title = 'Playground access missing';
      description = 'Practice scenarios and AI-powered training simulations are premium features. Subscribe to start improving your sales skills.';
    } else if (path?.includes('create-new-document') || path?.includes('knowledge-base')) {
      title = 'Content creation Restricted';
      description = 'Creating new playbooks and managing your knowledge base requires an active subscription. Unlock unlimited storage today.';
    }

    this.router.navigate(['/main/subscription-required'], {
      queryParams: { title, description, pricingButtonLabel: 'Start Free Trial', showSubBadges, showBackButton },
    });
  }

  private navigateToAdminRequired(path: string | undefined, showSubBadges: boolean, showBackButton?: boolean) {
    this.router.navigate(['/main/subscription-required'], {
      queryParams: {
        title: 'Administrator Access Required',
        description:
          'Only administrators can access subscription and billing settings. Please contact your administrator if you need to make changes.',
        showSubBadges: false,
        showBackButton,
      },
    });
  }

  private navigateToUpgradeRequired(path: string | undefined, showSubBadges: boolean, showBackButton?: boolean) {
    let title = 'Upgrade Required';
    let description = 'Your current plan does not include this feature. Please upgrade to a plan that supports it.';

    if (path?.includes('assistant')) {
      title = 'Live Assistant not included';
      description = 'The live assistant feature is not available on your current plan. Upgrade to unlock real-time guidance and meeting summaries.';
    } else if (path?.includes('playground')) {
      title = 'Playground not included';
      description =
        'Practice scenarios and AI-powered training simulations are not available on your current plan. Upgrade to start improving your sales skills.';
    } else if (path?.includes('create-new-document') || path?.includes('knowledge-base')) {
      title = 'Content creation not included';
      description =
        'Creating new playbooks and managing your knowledge base is not available on your current plan. Upgrade to unlock content creation.';
    }

    this.router.navigate(['/main/subscription-required'], {
      queryParams: { title, description, pricingButtonLabel: 'Upgrade Plan', showSubBadges, showBackButton },
    });
  }

  private navigateToLimitReached(path: string | undefined, showSubBadges: boolean, showBackButton?: boolean) {
    let title = 'Plan Limit Reached';
    let description = 'Your organization has reached its usage limit for this feature. Please upgrade your plan or wait for the next billing cycle.';

    if (path?.includes('assistant')) {
      title = 'Live Assistant Limit Reached';
      description =
        'You have used all the live assistant minutes included in your plan. Upgrade for more minutes or wait until your next billing period.';
    } else if (path?.includes('playground')) {
      title = 'Playground Limit Reached';
      description = 'You have reached your limit for practice sessions this month. Upgrade to continue practicing and improving your skills.';
    }

    this.router.navigate(['/main/subscription-required'], {
      queryParams: { title, description, pricingButtonLabel: 'Increase Limits', showSubBadges, showBackButton },
    });
  }
}
